<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <nav class="breadcrumb-nav mb-2">
                    <a href="/" class="text-muted">Home</a>
                    <span class="mx-2 text-muted">/</span>
                    <span class="text-gradient">Wallet</span>
                </nav>

                <div class="d-flex align-items-center mb-2 flex-wrap gap-2">
                    <h1 class="mb-0">
                        <span class="text-gradient" v-if="accountLabel">{{ accountLabel }}</span>
                        <span class="text-gradient" v-else>Wallet Explorer</span>
                    </h1>
                    <span v-if="accountLabel" class="compass-badge compass-badge--purple ms-2">{{ accountType }}</span>
                </div>

                <div class="d-flex align-items-center gap-2 mb-3 flex-wrap">
                    <p class="font-mono text-muted mb-0" style="font-size: 0.9rem;">
                        <a :href="`https://solscan.io/account/${address}`" target="_blank" class="text-gradient">
                            {{ address }}
                        </a>
                    </p>
                    <button class="compass-btn compass-btn--sm" @click="copyAddress" :title="copied ? 'Copied!' : 'Copy address'">
                        {{ copied ? 'Copied' : 'Copy' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">SOL Balance</div>
                <div class="hero-value">{{ formatSolBalance(solBalance) }}</div>
                <div class="hero-subtitle" v-if="solValueUsd > 0">${{ formatCompact(solValueUsd) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Portfolio Value</div>
                <div class="hero-value">${{ formatCompact(totalValueUsd) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--cyan">
                <div class="hero-label">Tokens Held</div>
                <div class="hero-value">{{ tokenCount }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue" v-if="tradeCount > 0">
                <div class="hero-label">Recent Trades</div>
                <div class="hero-value">{{ formatCompact(tradeCount) }}</div>
            </div>
        </div>

        <!-- Tab Navigation -->
        <div class="validator-tabs mb-4">
            <div class="btn-group">
                <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    class="compass-btn"
                    :class="{ 'compass-btn--primary': activeTab === tab.key }"
                    @click="switchTab(tab.key)"
                >
                    {{ tab.label }}
                    <span v-if="tab.count !== null" class="ms-1 opacity-75">({{ tab.count }})</span>
                </button>
            </div>
        </div>

        <!-- Tab Content -->
        <div v-show="activeTab === 'holdings'">
            <wallet-holdings :address="address" @loaded="onHoldingsLoaded" />
        </div>
        <div v-show="activeTab === 'activity'">
            <wallet-activity :address="address" @loaded="onActivityLoaded" />
        </div>
        <div v-show="activeTab === 'trades'">
            <wallet-trades :address="address" @loaded="onTradesLoaded" />
        </div>
        <div v-show="activeTab === 'transfers'">
            <wallet-transfers :address="address" />
        </div>
    </div>
</template>

<script>
import { formatCompact, formatPubkey } from '../composables/useFormatters';

export default {
    name: 'WalletExplorer',
    props: {
        address: { type: String, required: true },
    },
    data() {
        return {
            activeTab: 'holdings',
            copied: false,
            solBalance: null,
            solValueUsd: 0,
            totalValueUsd: 0,
            tokenCount: 0,
            tradeCount: 0,
            accountLabel: null,
            accountType: null,
        };
    },
    computed: {
        tabs() {
            return [
                { key: 'holdings', label: 'Holdings', count: this.tokenCount || null },
                { key: 'activity', label: 'Activity', count: null },
                { key: 'trades', label: 'Trades', count: this.tradeCount || null },
                { key: 'transfers', label: 'Transfers', count: null },
            ];
        },
    },
    methods: {
        formatCompact,
        formatPubkey,
        switchTab(tab) {
            this.activeTab = tab;
            window.history.replaceState(null, '', `#${tab}`);
        },
        copyAddress() {
            navigator.clipboard.writeText(this.address);
            this.copied = true;
            setTimeout(() => { this.copied = false; }, 2000);
        },
        formatSolBalance(val) {
            if (val === null) return '...';
            if (val >= 1000) return formatCompact(val);
            return val.toFixed(4);
        },
        onHoldingsLoaded(data) {
            this.tokenCount = data.total || 0;
            this.totalValueUsd = data.totalValueUsd || 0;

            // Find SOL in holdings
            const solHolding = (data.holdings || []).find(
                h => h.symbol === 'SOL' || h.mint === 'So11111111111111111111111111111111111111112'
            );
            if (solHolding) {
                this.solBalance = solHolding.balance;
                this.solValueUsd = solHolding.value_usd || 0;
            } else {
                this.solBalance = 0;
                this.solValueUsd = 0;
            }
        },
        onActivityLoaded(data) {
            // optional: use if needed
        },
        onTradesLoaded(data) {
            this.tradeCount = data.total || 0;
        },
    },
    mounted() {
        // Activate tab from URL hash
        const hash = window.location.hash.replace('#', '');
        if (['holdings', 'activity', 'trades', 'transfers'].includes(hash)) {
            this.activeTab = hash;
        }
    },
};
</script>
